import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Cog, Gauge, ShieldCheck, PackageCheck, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6, ease: [0.16, 1, 0.3, 1] } }),
};

const machines = [
  { name: "Automatic Pouch Making Machine", desc: "Precision sealing and cutting line for readymade and custom pouches.", image: "https://customer-assets.emergentagent.com/job_0a2bc895-7b15-4ac3-bad9-8999ca920027/artifacts/555eg9yz_WhatsApp%20Image%202026-04-12%20at%2012.48.01.jpeg", specs: "Servo Controlled | High-Speed Output" },
  { name: "Flexographic Printing Press", desc: "Multi-color surface printing for sharp brand graphics on plastic packaging films.", image: "https://customer-assets.emergentagent.com/job_0a2bc895-7b15-4ac3-bad9-8999ca920027/artifacts/jyki826a_WhatsApp%20Image%202026-04-12%20at%2012.48.02.jpeg", specs: "Up to 8 Colors | Excellent Registration" },
  { name: "Automatic Bottom Sealing Machine", desc: "Consistent side-seal and bottom-seal bag production for garment and industrial packaging.", image: "/images/machine-side-sealing.jpeg", specs: "Uniform Sealing | Continuous Production" },
  { name: "Rotogravure Printing Machine", desc: "Accurate roll handling and finishing for clean dimensions before dispatch.", image: "/images/machine-roll-cutting.jpeg", specs: "Clean Trim | Production-Ready Finish" },
];

const capabilities = [
  { icon: <Gauge size={20} />, title: "Production Capacity", value: "10M+ units/month", desc: "Scalable production lines to meet any volume requirement" },
  { icon: <Cog size={20} />, title: "Machinery", value: "10+ Machines", desc: "Modern equipment with automated quality monitoring" },
  { icon: <ShieldCheck size={20} />, title: "Quality Control", value: "99.5% Pass Rate", desc: "Multi-stage inspection with zero-defect targets" },
  { icon: <PackageCheck size={20} />, title: "Warehouse", value: "20,000+ sq ft", desc: "Climate-controlled storage for raw and finished goods" },
];

export default function InfrastructurePage() {
  return (
    <div data-testid="infrastructure-page" className="pt-16">
      <SEO
        title="Infrastructure & Machinery"
        description="Take a look at JSY's infrastructure, production capacity, quality control process, and advanced packaging machinery in Kolkata."
        image="/images/factory-floor-interior.jpeg"
      />
      {/* Hero */}
  <section className="relative py-12 px-6 bg-[#09090B] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="/images/machine-flexo-printing.jpeg"
            alt="" className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <span className="overline" style={{ color: '#16A34A' }}>Infrastructure</span>
            <h1 className="mt-3 text-5xl tracking-tighter" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800 }}>
              Our Manufacturing<br />Powerhouse
            </h1>
            <p className="mt-4 text-[#A1A1AA] max-w-lg">
              A modern facility on B.B.T. Road, Maheshtala, equipped with the latest machinery, powered by a team dedicated to precision and quality.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Capabilities */}
  <section className="py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0">
            {capabilities.map((c, i) => (
              <motion.div key={c.title} data-testid={`capability-${i}`} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i} variants={fadeUp} className="bento-card p-8 bg-white text-center">
                <div className="w-10 h-10 rounded flex items-center justify-center bg-[#EBF0FF] text-[#1D4ED8] mx-auto mb-4">{c.icon}</div>
                <p className="text-2xl tracking-tight" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800 }}>{c.value}</p>
                <h3 className="text-sm tracking-tight mt-1 mb-2" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>{c.title}</h3>
                <p className="text-xs text-[#71717A]">{c.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Machinery */}
  <section className="py-12 px-6 bg-[#F4F5F7]">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mb-8">
            <span className="overline">Machinery</span>
            <h2 className="mt-3 text-3xl tracking-tighter" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700 }}>
              Our Equipment
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {machines.map((m, i) => (
              <motion.div key={m.name} data-testid={`machine-${i}`} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i} variants={fadeUp} className="bg-white border border-[#E4E4E7] rounded overflow-hidden">
                <div className="h-56 overflow-hidden">
                  <img src={m.image} alt={m.name} className="w-full h-full object-cover" />
                </div>
                <div className="p-6">
                  <h3 className="text-base tracking-tight" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>{m.name}</h3>
                  <p className="mt-2 text-sm text-[#71717A]">{m.desc}</p>
                  <p className="mt-3 text-xs text-[#1D4ED8] font-medium tracking-wide">{m.specs}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quality */}
      <section className="py-12 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <span className="overline">Quality Control</span>
            <h2 className="mt-3 text-3xl tracking-tighter" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700 }}>
              Zero-Defect Manufacturing
            </h2>
            <p className="mt-4 text-[#71717A] leading-relaxed">
              Quality isn't just a checkpoint - it's embedded in every stage of our process. From incoming raw material inspection to final packaging audits, we maintain strict adherence to international standards.
            </p>
            <ul className="mt-6 space-y-3">
              {["Incoming Material Inspection", "In-Process Quality Monitoring", "Thickness & Clarity Testing", "Print Quality Verification", "Final Product Audit", "Packaging & Dispatch Check"].map((item) => (
                <li key={item} className="flex items-center gap-3 text-sm">
                  <div className="w-5 h-5 rounded-sm bg-[#F0FDF4] flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-[#16A34A]"></div>
                  </div>
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="rounded overflow-hidden border border-[#E4E4E7] h-[220px] md:h-[260px] lg:h-[280px]">
            <img src="https://images.pexels.com/photos/5860937/pexels-photo-5860937.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Warehouse" className="w-full h-full object-cover" />
          </motion.div>
        </div>
      </section>

      {/* CTA */}
  <section className="py-12 px-6 bg-[#09090B] text-white text-center">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
          <h2 className="text-3xl tracking-tighter" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700 }}>Impressed? Let's Work Together</h2>
          <Link to="/contact" className="btn-primary mt-6 inline-flex">Request a Factory Tour <ArrowRight size={16} /></Link>
        </motion.div>
      </section>
    </div>
  );
}
